import {
	createActionsHook,
	createHook,
	createStore,
	defaultRegistry,
	type BoundActions,
	type HookActionsFunction,
	type HookFunction,
	type StoreActionApi,
} from 'react-sweet-state';

import type {
	ApiTaskSSEEventType,
	ApiTaskSSEUpdate,
	ApiTaskSnapshotSSEUpdate,
	ApiTaskStatus,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';

export type TasksStoreState = {
	/** Latest SSE payload per `task_id` from `GET /rovo/v1/tasks/stream`. */
	updatesByTaskId: Map<string, ApiTaskSSEUpdate>;
};

const initialState: TasksStoreState = {
	updatesByTaskId: new Map(),
};

/**
 * A task in one of these states is finished and must never visually regress to
 * a non-terminal state, even if a stale `task_snapshot` says otherwise.
 */
const TERMINAL_STATUSES: ReadonlySet<ApiTaskStatus> = new Set<ApiTaskStatus>([
	'COMPLETED',
	'FAILED',
	'CANCELLED',
]);

const isTerminal = (status: ApiTaskStatus): boolean => TERMINAL_STATUSES.has(status);

/**
 * Returns true if `incoming` should overwrite `existing` for the same task_id.
 *
 * Monotonic rules (in priority order):
 *   1. If there is no existing entry, the incoming wins.
 *   2. If the existing entry is in a terminal status (COMPLETED / FAILED /
 *      CANCELLED), it is sticky — the only update that can replace it is
 *      another terminal update with a strictly newer `updated_at`.
 *   3. Otherwise compare `updated_at` and keep the newer one. Equal timestamps
 *      let the incoming win (matches the previous "last write wins" semantic
 *      for `task_status` events that all share a sub-millisecond timestamp).
 *
 * `updated_at` is an ISO-8601 string from the backend; lexical compare is
 * equivalent to chronological compare for ISO-8601 timestamps with consistent
 * precision and timezone, which is what the backend emits.
 */
function shouldOverwrite(
	existing: ApiTaskSSEUpdate | undefined,
	incoming: ApiTaskSSEUpdate,
): boolean {
	if (!existing) {
		return true;
	}
	if (isTerminal(existing.status)) {
		// Only a newer terminal update may replace a terminal entry.
		return isTerminal(incoming.status) && incoming.updated_at > existing.updated_at;
	}
	return incoming.updated_at >= existing.updated_at;
}

function mapWithIncrementalUpdate(
	prev: Map<string, ApiTaskSSEUpdate>,
	event: ApiTaskSSEUpdate,
): Map<string, ApiTaskSSEUpdate> {
	if (!shouldOverwrite(prev.get(event.task_id), event)) {
		return prev;
	}
	const next = new Map(prev);
	next.set(event.task_id, event);
	return next;
}

/**
 * Merges a `task_snapshot` payload into the existing map.
 *
 * The merge here uses `shouldOverwrite` so each task's entry is kept unless the
 * snapshot strictly improves on it (newer `updated_at`, and never regressing
 * past a terminal status). Tasks present in the existing map but absent from
 * the snapshot are RETAINED — REST refresh remains the authority for removing
 * tasks, not the SSE snapshot.
 */
function mergeSnapshotTasks(
	prev: Map<string, ApiTaskSSEUpdate>,
	tasks: ApiTaskSSEUpdate[],
): Map<string, ApiTaskSSEUpdate> {
	const next = new Map(prev);
	let changed = false;
	for (const incoming of tasks) {
		if (shouldOverwrite(next.get(incoming.task_id), incoming)) {
			next.set(incoming.task_id, incoming);
			changed = true;
		}
	}
	return changed ? next : prev;
}

type TasksActions = {
	/**
	 * Routes Tasks SSE payloads by envelope `eventType`:
	 * - `task_snapshot` — monotonically merges `tasks[]` into the in-memory map
	 *   (newer `updated_at` wins; terminal statuses are sticky). NOT a wholesale
	 *   replacement, so a stale snapshot cannot regress live state.
	 * - `task_status` — merges one `ApiTaskSSEUpdate` by `task_id` using the
	 *   same monotonic rules.
	 * - `connected` — ignored here (handshake only).
	 */
	ingestTasksSSEEvent: (
		event: ApiTaskSSEUpdate | ApiTaskSnapshotSSEUpdate | Record<string, unknown>,
		eventType: ApiTaskSSEEventType,
	) => ({ getState, setState }: StoreActionApi<TasksStoreState>) => void;

	applyTaskUpdate: (
		event: ApiTaskSSEUpdate,
	) => ({ getState, setState }: StoreActionApi<TasksStoreState>) => void;

	replaceAllFromSnapshot: (
		tasks: ApiTaskSSEUpdate[],
	) => ({ getState, setState }: StoreActionApi<TasksStoreState>) => void;

	/**
	 * Drops all SSE entries belonging to a conversation (e.g. on delete) and
	 * remembers the conversation as deleted so future SSE pushes / stale REST
	 * entries for the same conversation are filtered out by consumers.
	 */
	removeTasksForConversation: (
		conversationId: string,
	) => ({ getState, setState }: StoreActionApi<TasksStoreState>) => void;
};

const store = createStore({
	name: 'tasks',
	initialState,
	actions: {
		ingestTasksSSEEvent:
			(
				event: ApiTaskSSEUpdate | ApiTaskSnapshotSSEUpdate | Record<string, unknown>,
				eventType: ApiTaskSSEEventType,
			): (({ getState, setState }: StoreActionApi<TasksStoreState>) => void) =>
			({ getState, setState }) => {
				if (eventType === 'task_snapshot') {
					const snapshot = event as ApiTaskSnapshotSSEUpdate;
					setState({
						updatesByTaskId: mergeSnapshotTasks(getState().updatesByTaskId, snapshot.tasks ?? []),
					});
					return;
				}

				if (eventType === 'task_status') {
					setState({
						updatesByTaskId: mapWithIncrementalUpdate(
							getState().updatesByTaskId,
							event as ApiTaskSSEUpdate,
						),
					});
				}
			},

		applyTaskUpdate:
			(
				event: ApiTaskSSEUpdate,
			): (({ getState, setState }: StoreActionApi<TasksStoreState>) => void) =>
			({ getState, setState }) => {
				setState({
					updatesByTaskId: mapWithIncrementalUpdate(getState().updatesByTaskId, event),
				});
			},

		replaceAllFromSnapshot:
			(
				tasks: ApiTaskSSEUpdate[],
			): (({ getState, setState }: StoreActionApi<TasksStoreState>) => void) =>
			({ getState, setState }) => {
				setState({
					updatesByTaskId: mergeSnapshotTasks(getState().updatesByTaskId, tasks),
				});
			},

		removeTasksForConversation:
			(
				conversationId: string,
			): (({ getState, setState }: StoreActionApi<TasksStoreState>) => void) =>
			({ getState, setState }) => {
				const prev = getState().updatesByTaskId;
				let next: Map<string, ApiTaskSSEUpdate> | null = null;
				for (const [taskId, entry] of prev) {
					if (entry.conversation_id === conversationId) {
						if (next === null) {
							next = new Map(prev);
						}
						next.delete(taskId);
					}
				}
				if (next !== null) {
					setState({ updatesByTaskId: next });
				}
			},
	},
});

type TasksBoundActions = BoundActions<TasksStoreState, TasksActions>;

export const useTasksStore: HookFunction<TasksStoreState, TasksBoundActions, void> =
	createHook(store);

export const useTasksStoreActions: HookActionsFunction<TasksBoundActions> =
	createActionsHook(store);

/**
 * Subscribe only to `updatesByTaskId` — avoids re-rendering on unrelated tasks store changes.
 */
export const useTasksUpdates: HookFunction<
	Map<string, ApiTaskSSEUpdate>,
	TasksBoundActions,
	void
> = createHook(store, {
	selector: (state: TasksStoreState) => state.updatesByTaskId,
});

export const removeTasksForConversation = (conversationId: string): void => {
	defaultRegistry.getStore(store).actions.removeTasksForConversation(conversationId);
};
